import React, { useState } from 'react';
import paymentService from '../services/paymentService';

const PaymentLinkGenerator = () => {
  const [amountUsd, setAmountUsd] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentLink, setPaymentLink] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!amountUsd || parseFloat(amountUsd) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Generate payment link
      const result = await paymentService.createPaymentLink(parseFloat(amountUsd), {
        email,
        name
      });
      
      setPaymentLink(result);
    } catch (err) {
      console.error('Error generating payment link:', err);
      setError(err.message || 'Failed to generate payment link');
    } finally {
      setLoading(false);
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  
  const resetForm = () => {
    setPaymentLink(null);
    setError(null);
  };
  
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-bold mb-4">Generate Payment Link</h2>
      
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
          <p>{error}</p>
        </div>
      )}
      
      {paymentLink ? (
        <div className="space-y-4">
          <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4">
            <p className="font-bold">Payment link generated successfully!</p>
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Link
            </label>
            <div className="flex items-center">
              <input
                type="text"
                value={paymentLink.payment_link}
                readOnly
                className="flex-1 border border-gray-300 rounded-l-md p-2 bg-gray-50"
              />
              <button
                onClick={() => copyToClipboard(paymentLink.payment_link)}
                className={`px-4 py-2 rounded-r-md ${copied ? 'bg-green-600' : 'bg-blue-600'} text-white`}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
          
          <div className="bg-gray-100 p-4 rounded-md">
            <h3 className="text-lg font-medium mb-2">Payment Details</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-sm text-gray-600">Amount</p>
                <p className="font-bold">${parseFloat(paymentLink.amount_usd).toFixed(2)} USD</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <p className="font-bold capitalize">{paymentLink.status}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Reference ID</p>
                <p className="font-mono text-sm">{paymentLink.reference_id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Created</p>
                <p className="text-sm">{new Date(paymentLink.created_at).toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="flex justify-between mt-4">
            <button
              onClick={resetForm}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
            >
              Generate Another Link
            </button>
            
            <a
              href={paymentLink.payment_link}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
            >
              Test Payment Page
            </a>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label htmlFor="amountUsd" className="block text-sm font-medium text-gray-700 mb-1">
              Amount (USD) *
            </label>
            <input
              id="amountUsd"
              type="number"
              min="0.01"
              step="0.01"
              value={amountUsd}
              onChange={(e) => setAmountUsd(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2"
              placeholder="50.00"
              required
            />
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Customer Email (optional)
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2"
              placeholder="customer@example.com"
            />
          </div>
          
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Customer Name (optional)
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2"
              placeholder="John Doe"
            />
          </div>
          
          <div className="pt-4">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Generating...' : 'Generate Payment Link'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentLinkGenerator;